import { CSSProperties, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CreateBrandPayload } from 'api/brandApi';
import ModalSymphony from 'elements/ModalSymphony';
import SpotifyArtistSearch from 'components/shareable/SpotifyArtistSearch';
import { CurrentBrand, InstagramProfileForOnboarding, SpotifyArtistSearchResponse, TikTokProfileForOnboarding } from 'modules/Types';
import Colors from 'modules/Colors';
import { clsx, makeKey, styx } from 'modules/Utils';
import SocialConnectionsButtonsWithModals from 'components/shareable/SocialConnectionsButtonsWithModals';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import { PlanType } from 'modules/Const';
import TabsSymphony, { TabItem } from 'elements/TabsSymphony';
import TabSymphony from 'elements/TabSymphony';
import CustomAvatar from 'components/ProfileDropdown/CustomAvatar';
import BrandTier from 'elements/BrandTier';
import Sty from 'modules/Sty';
import Loader from 'elements/Loader';
import useBrand from 'Hooks/useBrand';
import Banner from 'elements/Banner';
import useScreen from 'Hooks/useScreen';
import LoaderOverlay from 'elements/LoaderOverlay';

const ADD_BRAND_TABS: TabItem[] = [
    {
        id: 'existingBrand',
        title: 'Existing Brand',
    },
    {
        id: 'newBrand',
        title: 'New Brand',
    }
]

interface AddBrandToOrganizationModalProps {
    /** Optional class name for styling */
    className?: string;
    /** Controls modal visibility */
    isOpen: boolean;
    /** Handler for closing the modal */
    onClose: () => void;
    /** Callback fired when artist is successfully added to organization */
    onFinishAddingToOrganization?: () => void;
}

const AddBrandToOrganizationModal = ({
    className,
    isOpen,
    onClose,
    onFinishAddingToOrganization = () => { },
}: AddBrandToOrganizationModalProps) => {
    const history = useHistory();
    const {
        mobileView,
    } = useScreen()

    const {
        createBrand,
        setCurrentBrand,
        loadedBrands,
    } = useBrand();

    const {
        organization,
        organizationBrandIds,
        createOrganizationBrand,
        isLoadingOrganizationAction,
        setIsLoadingAddBrandToOrganization,
        isLoadingAddBrandToOrganization,
    } = useOrganizationV2();

    const [selectedAddBrandTab, setSelectedAddBrandTab] = useState<TabItem>(ADD_BRAND_TABS[0]);

    const createBrand_local = async (brandPayload: CreateBrandPayload, existingBrand?: CurrentBrand | null) => {
        setIsLoadingAddBrandToOrganization(true)
        let brandId = existingBrand?.id
        try {
            if (!existingBrand) {
                const newBrand = await createBrand(brandPayload)
                brandId = newBrand.id
            }

            await createOrganizationBrand({
                organizationId: organization?.id!,
                brandId: brandId!
            })

            onFinishAddingToOrganization();
            onClose();
        } catch (error) {
            console.error('Failed to create brand:', error);
        } finally {
            setIsLoadingAddBrandToOrganization(false)
        }
    }

    const handleSelectArtist = async (
        artist: SpotifyArtistSearchResponse | null,
        existingBrand?: CurrentBrand | null
    ) => {
        if (!artist) {
            return
        }

        const brandPayload: CreateBrandPayload = {
            name: artist.name,
            customization: {
                spotify_artist: artist
            },
            tier: PlanType.TEAM
        }

        await createBrand_local(brandPayload, existingBrand)
    }

    const handleCreateNewProfile = async (name: string) => {
        const brandPayload: CreateBrandPayload = {
            name,
            customization: {},
            tier: PlanType.TEAM
        }
        await createBrand_local(brandPayload)
    }

    const handleCreateNewBrandTikTok = async (profile: TikTokProfileForOnboarding) => {
        const brandPayload: CreateBrandPayload = {
            name: profile.username,
            customization: {
                tiktok: profile
            },
            tier: PlanType.TEAM
        }

        await createBrand_local(brandPayload)
    }

    const handleCreateNewBrandInstagram = async (profile: InstagramProfileForOnboarding) => {
        const brandPayload: CreateBrandPayload = {
            name: profile.username,
            customization: {
                instagram_profile_for_onboarding: profile
            },
            tier: PlanType.TEAM
        }

        await createBrand_local(brandPayload)
    }

    const addExistingBrand = async (brandId: number) => {
        if (isLoadingOrganizationAction) {
            return;
        }

        await createOrganizationBrand({
            organizationId: organization?.id!,
            brandId: brandId,
        })

        onClose()
    }

    const renderExistingBrandsTab = () => {
        const brandsFiltered = loadedBrands?.filter((brand) => !organizationBrandIds?.includes(brand.id)) || []
        return (
            <TabSymphony
                className="addBrandToOrganizationModalTab"
                visible={selectedAddBrandTab.id === 'existingBrand'}
                style={{ gap: 8 }}
            >
                {brandsFiltered.length > 0 ?
                    <>
                        <div className="addBrandToOrganizationModalExistingBrandsHeader">
                            Select a brand to add to your team
                        </div>
                        <div className="addBrandToOrganizationModalExistingBrands" style={sty.addBrandToOrganizationModalExistingBrands}>
                            {brandsFiltered.map((brand, index) => {
                                const {
                                    id,
                                    name,
                                    image,
                                    tier,
                                } = brand

                                return (
                                    <div
                                        key={makeKey(id, index)}
                                        className="addBrandToOrganizationModalBrand"
                                        style={styx(
                                            sty.addBrandToOrganizationModalBrand,
                                            {
                                                borderBottom: index === brandsFiltered.length - 1 ? 'none' : Sty.border,
                                            },
                                            isLoadingOrganizationAction ? { opacity: 0.5, cursor: 'not-allowed' } : {}
                                        )}
                                        onClick={() => addExistingBrand(id)}
                                    >
                                        <CustomAvatar
                                            className="addBrandToOrganizationModalBrandAvatar"
                                            image={image}
                                            brandOverride={brand}
                                        />
                                        <div className="addBrandToOrganizationModalBrandName">
                                            {name}
                                        </div>
                                        <BrandTier
                                            className="addBrandToOrganizationModalBrandTierTag"
                                            tier={tier}
                                        />
                                        {isLoadingOrganizationAction &&
                                            <Loader
                                                className="addBrandToOrganizationModalBrandLoader"
                                                style={{ marginLeft: 'auto' }}
                                            />
                                        }
                                    </div>
                                )
                            })}
                        </div>
                    </>
                    :
                    <Banner
                        className="addBrandToOrganizationModalNoBrandsBanner"
                        title="All of your brands are already in your team."
                    />
                }
            </TabSymphony>
        )
    }

    const renderNewBrandTab = () => {
        return (
            <TabSymphony
                className="addBrandToOrganizationModalTab"
                visible={selectedAddBrandTab.id === 'newBrand'}
            >
                <SpotifyArtistSearch
                    className="spotifyArtistSearch"
                    title="Enter your artist name or Spotify URL"
                    styleArtistList={{
                        border: Sty.border,
                        borderRadius: 16,
                        padding: mobileView ? 8 : 16,
                        backgroundColor: Colors.greyLight,
                        boxShadow: Sty.boxShadowInset,
                    }}
                    heightArtistList={mobileView ? undefined : 500}
                    onSelectArtist={handleSelectArtist}
                    onClickCreateNewProfile={handleCreateNewProfile}
                    isSetCurrentBrand={false}
                />

                <SocialConnectionsButtonsWithModals
                    selectedTikTokProfile={null}
                    selectedInstagramProfile={null}
                    onTikTokSelect={handleCreateNewBrandTikTok}
                    onInstagramSelect={handleCreateNewBrandInstagram}
                    onSignupWithoutConnections={handleCreateNewProfile}
                />
            </TabSymphony>
        )
    }

    return (
        <>
            <style>
                {`
                    .addBrandToOrganizationModalBrand:hover {
                        background-color: ${Colors.greyLight};
                    }
                `}
            </style>
            {isLoadingAddBrandToOrganization &&
                <LoaderOverlay
                    className="addBrandToOrganizationModalLoader"
                    title="Adding brand..."
                    isTransparent
                />
            }
            <ModalSymphony
                className={clsx('addNewArtistModalComponent', className)}
                isOpen={isOpen}
                onClose={onClose}
                title="Add a Brand"
                subtitle="Add a brand to your team."
                headerIcon="tada"
            >
                <TabsSymphony
                    className="addBrandToOrganizationModalTabs"
                    tabs={ADD_BRAND_TABS}
                    selectedTab={selectedAddBrandTab}
                    onChange={(tab) => {
                        setSelectedAddBrandTab(tab as TabItem)
                    }}
                    isBorderBottom
                    isMarginBottom
                />
                {renderExistingBrandsTab()}
                {renderNewBrandTab()}
            </ModalSymphony>
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    anotherSocialPlatformContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        width: '100%',
        gap: 8
    },
    createAccountWithoutSpotifyText2: {
        fontSize: 18,
        color: '#707070',
        marginTop: 32
    },
    platformsButtonsContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        gap: 16
    },
    platformButtonYoutubeContainer: {
        display: 'grid',
        placeItems: 'center',
        width: 48,
        height: 48,
        borderRadius: 12,
        cursor: 'pointer',
        background: Colors.BACKGROUND_YOUTUBE_ICON
    },
    platformButtonTikTokContainer: {
        display: 'grid',
        placeItems: 'center',
        width: 48,
        height: 48,
        borderRadius: 12,
        cursor: 'pointer',
        background: Colors.BACKGROUND_TIKTOK_ICON
    },
    platformButtonInstagramContainer: {
        display: 'grid',
        placeItems: 'center',
        width: 48,
        height: 48,
        borderRadius: 12,
        cursor: 'pointer',
        background: Colors.GRADIENT_INSTAGRAM
    },
    addBrandToOrganizationModalExistingBrands: {
        maxHeight: 300,
        overflowY: 'auto',
        border: Sty.border,
        borderRadius: 16,
    },
    addBrandToOrganizationModalBrand: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: 16,
        cursor: 'pointer',
    },
};

export default AddBrandToOrganizationModal;